// Tiny fetch wrapper. Auth header logic:
//
// 1. If a Firebase user is signed in, attach `Authorization: Bearer <idToken>`
//    (preferred path — production).
// 2. Otherwise, IF the user has explicitly picked a dev persona via the
//    Sign-in fallback (or set localStorage['cura-studio:dev-user'] manually),
//    fall back to `x-dev-user: <email>` for dev-bypass testing.
//
// IMPORTANT: do NOT auto-seed a default dev persona — anonymous visitors
// must hit the landing page, not get instantly logged in.

import { currentIdToken, firebaseConfigured, waitForAuthReady } from "@/lib/firebase";

const BASE = import.meta.env.VITE_API_URL || "";

// Source of truth for the STUDIO_ADMIN "scope into this tenant" selection.
// Read on every request so the value can change mid-session without a reload.
export const ACTIVE_TENANT_LS_KEY = "cura-studio:active-tenant-id";

function readActiveTenant(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(ACTIVE_TENANT_LS_KEY);
}

type FetchOpts = Omit<RequestInit, "body"> & {
  body?: unknown;
  // Dev-mode override: caller can specify the user email to impersonate.
  devUser?: string;
};

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function api<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!headers.has("content-type") && opts.body !== undefined) {
    headers.set("content-type", "application/json");
  }

  // Prefer Firebase ID token if signed in; otherwise fall through to dev-user.
  // Wait for Firebase to hydrate the persisted session before reading the
  // current user — otherwise we'd race and send no Authorization header on
  // the first paint (cue 401 storms in the console).
  if (firebaseConfigured()) await waitForAuthReady();
  const token = firebaseConfigured() ? await currentIdToken() : null;
  const devUser =
    opts.devUser ??
    (typeof window !== "undefined"
      ? window.localStorage.getItem("cura-studio:dev-user")
      : null);
  if (token) {
    headers.set("authorization", `Bearer ${token}`);
  } else if (devUser) {
    headers.set("x-dev-user", devUser);
  } else if (path.startsWith("/api/v1/") && typeof window !== "undefined") {
    // Auth-state desync safety net: we have neither a Firebase token nor a
    // dev persona, yet we're about to call a protected endpoint. Either
    // Firebase's React state and the SDK auth.currentUser diverged (stale
    // persistence, revoked token), or a SidebarLayout guard let an anonymous
    // visitor through. Wipe half-state and bounce to the LANDING (not
    // /auth/signin — the user should see the splash and choose Sign in).
    if (window.location.pathname !== "/") {
      console.warn("[api] No auth header available — redirecting to landing.");
      window.localStorage.removeItem("cura-studio:dev-user");
      window.localStorage.removeItem("cura-studio:active-tenant-id");
      window.location.assign("/");
    }
    throw new ApiError(401, "Auth state lost — redirecting", null);
  }

  // STUDIO_ADMIN tenant-override: when the admin has scoped into a client
  // tenant via the sidebar switcher, every /api/v1 request carries
  // `x-active-tenant: <tenantId>`. The backend ignores it for non-admins,
  // so it's safe to send unconditionally for any signed-in caller.
  if (path.startsWith("/api/v1/")) {
    const active = readActiveTenant();
    if (active) headers.set("x-active-tenant", active);
  }

  const res = await fetch(BASE + path, {
    ...opts,
    headers,
    body:
      opts.body === undefined
        ? undefined
        : typeof opts.body === "string"
          ? opts.body
          : JSON.stringify(opts.body),
  });

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, `API ${res.status} ${res.statusText}`, body);
  }
  // 204 No Content
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
